<mat-card class="trinta-card sessions-card mb-25 bg-white border-none d-block">

  <!-- ── Sessions list header ── -->
  <ng-container *ngIf="!selectedSession">
    <mat-card-header>
      <mat-card-title>
        <h5 class="mt-0 mb-5">My Sessions</h5>
        <p class="subtitle">Create and manage your estimation sessions. Each session is a named subset of your products.</p>
      </mat-card-title>
    </mat-card-header>

    <mat-card-content>
      <div class="sessions-toolbar">
        <button mat-flat-button color="primary" (click)="openCreateSessionDialog()" class="new-session-btn">
          <mat-icon>add</mat-icon>
          New Session
        </button>
      </div>

      <div *ngIf="sessionsErrorMessage" class="alert alert-danger">{{ sessionsErrorMessage }}</div>

      <!-- Loading state -->
      <div class="loading-overlay-inline" *ngIf="isLoadingSessions">
        <mat-spinner diameter="44" strokeWidth="4" color="accent"></mat-spinner>
        <span>Loading sessions…</span>
      </div>

      <!-- Sessions table -->
      <div class="table-container" *ngIf="!isLoadingSessions">
        <div class="table-wrapper" *ngIf="sessions.length > 0">
          <table mat-table [dataSource]="sessions" class="sessions-table">

            <ng-container matColumnDef="name">
              <th mat-header-cell *matHeaderCellDef>Session Name</th>
              <td mat-cell *matCellDef="let session">
                <span class="session-name-link" (click)="selectSession(session)">{{ session.name }}</span>
              </td>
            </ng-container>

            <ng-container matColumnDef="product_count">
              <th mat-header-cell *matHeaderCellDef>Products</th>
              <td mat-cell *matCellDef="let session">{{ session.product_count }}</td>
            </ng-container>

            <ng-container matColumnDef="created_at">
              <th mat-header-cell *matHeaderCellDef>Created</th>
              <td mat-cell *matCellDef="let session">{{ session.created_at | date: 'mediumDate' }}</td>
            </ng-container>

            <ng-container matColumnDef="visibility">
              <th mat-header-cell *matHeaderCellDef>Visibility</th>
              <td mat-cell *matCellDef="let session">
                <span
                  class="visibility-badge"
                  [class.public]="session.visibility === 'public' || session.is_public"
                  [class.shared]="session.visibility === 'restricted'"
                  [class.private]="!session.visibility || session.visibility === 'private'"
                >
                  <mat-icon>{{ visibilityIcon(session) }}</mat-icon>
                  {{ visibilityLabel(session) }}
                </span>
              </td>
            </ng-container>

            <ng-container matColumnDef="status">
              <th mat-header-cell *matHeaderCellDef>Status</th>
              <td mat-cell *matCellDef="let session">
                <span *ngIf="isActiveSession(session.id)" class="active-badge">
                  <mat-icon class="active-badge-icon">check_circle</mat-icon>
                  Active
                </span>
              </td>
            </ng-container>

            <ng-container matColumnDef="col_select">
              <th mat-header-cell *matHeaderCellDef class="icon-col-header">Select</th>
              <td mat-cell *matCellDef="let session" class="icon-col-cell">
                <button
                  [class.select-btn]="!isActiveSession(session.id)"
                  [class.active-btn]="isActiveSession(session.id)"
                  mat-flat-button
                  [color]="isActiveSession(session.id) ? 'accent' : 'primary'"
                  (click)="toggleActiveSession(session)"
                  [disabled]="isActivatingSession(session.id)"
                  [matTooltip]="isActiveSession(session.id) ? 'Click to deselect this session' : 'Select for Estimator'"
                  class="action-btn-select"
                >
                  <mat-progress-spinner *ngIf="isActivatingSession(session.id)" diameter="16" mode="indeterminate"></mat-progress-spinner>
                  <ng-container *ngIf="!isActivatingSession(session.id)">
                    <mat-icon>{{ isActiveSession(session.id) ? 'check' : 'playlist_add' }}</mat-icon>
                    {{ isActiveSession(session.id) ? 'Active' : 'Select' }}
                  </ng-container>
                </button>
              </td>
            </ng-container>

            <ng-container matColumnDef="col_view">
              <th mat-header-cell *matHeaderCellDef class="icon-col-header">View</th>
              <td mat-cell *matCellDef="let session" class="icon-col-cell">
                <button
                  mat-icon-button
                  color="primary"
                  (click)="selectSession(session)"
                  matTooltip="View session products"
                >
                  <mat-icon>visibility</mat-icon>
                </button>
              </td>
            </ng-container>

            <ng-container matColumnDef="col_download">
              <th mat-header-cell *matHeaderCellDef class="icon-col-header">Download</th>
              <td mat-cell *matCellDef="let session" class="icon-col-cell">
                <button
                  mat-icon-button
                  color="primary"
                  (click)="downloadSessionCsv(session)"
                  [disabled]="isDownloadingSessionCsv"
                  matTooltip="Download as CSV"
                >
                  <img src="assets/feather_i/download-cloud.svg" alt="Download" class="btn-icon-img">
                </button>
              </td>
            </ng-container>

            <ng-container matColumnDef="col_delete">
              <th mat-header-cell *matHeaderCellDef class="icon-col-header">Delete</th>
              <td mat-cell *matCellDef="let session" class="icon-col-cell">
                <button
                  mat-icon-button
                  color="warn"
                  (click)="openDeleteSessionDialog(session)"
                  [disabled]="isDeletingSession(session.id)"
                  matTooltip="Delete session"
                >
                  <mat-progress-spinner *ngIf="isDeletingSession(session.id)" diameter="18" mode="indeterminate"></mat-progress-spinner>
                  <i-feather *ngIf="!isDeletingSession(session.id)" name="trash-2"></i-feather>
                </button>
              </td>
            </ng-container>

            <ng-container matColumnDef="col_share">
              <th mat-header-cell *matHeaderCellDef class="icon-col-header">Share</th>
              <td mat-cell *matCellDef="let session" class="icon-col-cell">
                <button
                  mat-icon-button
                  color="primary"
                  (click)="openShareModal(session)"
                  matTooltip="Manage sharing"
                >
                  <mat-icon>group</mat-icon>
                </button>
              </td>
            </ng-container>

            <tr mat-header-row *matHeaderRowDef="sessionColumns"></tr>
            <tr mat-row *matRowDef="let row; columns: sessionColumns;" class="session-row" [class.active-session-row]="isActiveSession(row.id)"></tr>
          </table>
        </div>

        <div class="empty-state" *ngIf="!sessions.length && !isLoadingSessions">
          <i-feather name="inbox"></i-feather>
          <p>No sessions yet. Create your first session to get started.</p>
          <button mat-flat-button color="primary" (click)="openCreateSessionDialog()">Create Session</button>
        </div>
      </div>
    </mat-card-content>
  </ng-container>

  <!-- ── Session detail (products table) ── -->
  <ng-container *ngIf="selectedSession">
    <mat-card-header>
      <mat-card-title>
        <div class="detail-header">
          <button mat-stroked-button (click)="backToSessions()" class="back-btn">
            <mat-icon>arrow_back</mat-icon>
            All Sessions
          </button>
          <div class="session-title-group">
            <h5 class="mt-0 mb-5">{{ selectedSession.name }}</h5>
            <p class="subtitle">
              {{ visibilityLabel(selectedSession) }} &nbsp;·&nbsp;
              Products in this session. Changes here do not affect the full product library.
            </p>
          </div>
          <div class="detail-actions">
            <button
              mat-flat-button
              color="primary"
              (click)="downloadSessionCsv(selectedSession)"
              [disabled]="isDownloadingSessionCsv"
              class="csv-btn"
            >
              <img src="assets/feather_i/download-cloud.svg" alt="Download" class="download-button-icon-white">
              Download CSV
            </button>
            <button mat-stroked-button color="accent" (click)="openShareModal(selectedSession)" class="share-detail-btn">
              <mat-icon>group</mat-icon>
              Manage Sharing
            </button>
            <button mat-flat-button color="warn" (click)="openDeleteSessionDialog(selectedSession)" class="delete-session-btn">
              <mat-icon>delete_outline</mat-icon>
              Delete Session
            </button>
          </div>
        </div>
      </mat-card-title>
    </mat-card-header>

    <mat-card-content style="overflow: visible !important; max-height: none !important;">
      <form class="filters-form" [formGroup]="filtersForm" (ngSubmit)="applyFilters()">
        <div class="filters-grid">
          <mat-form-field class="compact-field" appearance="outline">
            <mat-label>Brand</mat-label>
            <input matInput formControlName="brand">
          </mat-form-field>

          <mat-form-field class="compact-field" appearance="outline">
            <mat-label>Item</mat-label>
            <input matInput formControlName="item">
          </mat-form-field>

          <div class="filters-actions">
            <button mat-flat-button color="primary" type="submit">Apply</button>
            <button mat-stroked-button type="button" (click)="resetFilters()">Reset</button>
          </div>
        </div>
      </form>

      <div *ngIf="productErrorMessage" class="alert alert-danger">{{ productErrorMessage }}</div>

      <div class="table-toolbar" *ngIf="!isInitialLoad">
        <div class="results-info" *ngIf="totalProducts > 0">
          Showing
          <span class="results-highlight">{{ products.length }}</span>
          of
          <span class="results-highlight">{{ totalProducts }}</span>
          products
        </div>
      </div>

      <div class="table-container" [class.is-loading]="isLoadingProducts">
        <div class="loading-overlay" *ngIf="isLoadingProducts">
          <mat-spinner diameter="44" strokeWidth="4" color="accent"></mat-spinner>
          <span>Loading products…</span>
        </div>

        <div
          class="table-wrapper"
          *ngIf="products.length > 0"
          #tableWrapper
          (wheel)="onTableWheel($event)"
          (touchstart)="onTableTouchStart($event)"
          (touchmove)="onTableTouchMove($event)"
        >
          <table mat-table [dataSource]="products" class="images-table">
            <ng-container matColumnDef="image_name">
              <th mat-header-cell *matHeaderCellDef>Image ID</th>
              <td mat-cell *matCellDef="let image">
                <div
                  class="cell-preview-trigger"
                  [class.cell-preview-loading]="isPreviewing(image.id)"
                  [matTooltip]="isPreviewing(image.id) ? 'Loading preview…' : 'Click to preview image'"
                  matTooltipShowDelay="300"
                  (click)="previewImage(image)"
                >
                  <div class="cell-primary">
                    {{ image.id }}
                    <mat-spinner *ngIf="isPreviewing(image.id)" diameter="12" strokeWidth="2" class="inline-spinner"></mat-spinner>
                  </div>
                  <div class="cell-secondary" *ngIf="image.image_name">
                    {{ image.image_name?.split('/')?.pop() }}
                  </div>
                </div>
              </td>
            </ng-container>

            <ng-container matColumnDef="brand">
              <th mat-header-cell *matHeaderCellDef>Brand</th>
              <td mat-cell *matCellDef="let image">{{ image.brand || '—' }}</td>
            </ng-container>

            <ng-container matColumnDef="item">
              <th mat-header-cell *matHeaderCellDef>Item</th>
              <td mat-cell *matCellDef="let image">{{ image.item || '—' }}</td>
            </ng-container>

            <ng-container matColumnDef="material">
              <th mat-header-cell *matHeaderCellDef>Material</th>
              <td mat-cell *matCellDef="let image">{{ image.material || '—' }}</td>
            </ng-container>

            <ng-container matColumnDef="color">
              <th mat-header-cell *matHeaderCellDef>Color</th>
              <td mat-cell *matCellDef="let image">{{ image.color || '—' }}</td>
            </ng-container>

            <ng-container matColumnDef="product_age">
              <th mat-header-cell *matHeaderCellDef>Age</th>
              <td mat-cell *matCellDef="let image">{{ image.product_age || '—' }}</td>
            </ng-container>

            <ng-container matColumnDef="product_condition">
              <th mat-header-cell *matHeaderCellDef>Condition</th>
              <td mat-cell *matCellDef="let image">{{ image.product_condition || '—' }}</td>
            </ng-container>

            <ng-container matColumnDef="price">
              <th mat-header-cell *matHeaderCellDef>Price</th>
              <td mat-cell *matCellDef="let image">
                {{ image.price !== null && image.price !== undefined ? (image.price | currency:'USD':'symbol':'1.0-2') : '—' }}
              </td>
            </ng-container>

            <ng-container matColumnDef="uploaded_by">
              <th mat-header-cell *matHeaderCellDef>Uploaded by</th>
              <td mat-cell *matCellDef="let image">
                <span class="uploader-label">{{ image.uploaded_by_username || '—' }}</span>
              </td>
            </ng-container>

            <ng-container matColumnDef="edit">
              <th mat-header-cell *matHeaderCellDef class="actions-column">Edit</th>
              <td mat-cell *matCellDef="let image" class="actions-column">
                <button
                  mat-stroked-button
                  color="primary"
                  (click)="openEditProductDialog(image)"
                  matTooltip="Edit product details"
                  class="icon-only-button edit-button"
                >
                  <i-feather name="edit" class="edit-button-icon"></i-feather>
                </button>
              </td>
            </ng-container>

            <ng-container matColumnDef="actions">
              <th mat-header-cell *matHeaderCellDef class="actions-column">Download</th>
              <td mat-cell *matCellDef="let image" class="actions-column">
                <button
                  mat-stroked-button
                  color="primary"
                  (click)="downloadImage(image)"
                  [disabled]="isDownloading(image.id)"
                  matTooltip="Download image"
                  class="icon-only-button"
                >
                  <mat-progress-spinner
                    *ngIf="isDownloading(image.id)"
                    diameter="20"
                    mode="indeterminate"
                  ></mat-progress-spinner>
                  <img
                    *ngIf="!isDownloading(image.id)"
                    src="assets/feather_i/download-cloud.svg"
                    alt="Download"
                    class="download-button-icon"
                  />
                </button>
              </td>
            </ng-container>

            <ng-container matColumnDef="delete">
              <th mat-header-cell *matHeaderCellDef class="actions-column delete-column">Remove</th>
              <td mat-cell *matCellDef="let image" class="actions-column delete-column">
                <button
                  mat-stroked-button
                  color="warn"
                  (click)="openDeleteProductDialog(image)"
                  [disabled]="isDeleting(image.id)"
                  matTooltip="Remove from session"
                  class="icon-only-button delete-button"
                >
                  <mat-progress-spinner
                    *ngIf="isDeleting(image.id)"
                    diameter="20"
                    mode="indeterminate"
                  ></mat-progress-spinner>
                  <i-feather
                    *ngIf="!isDeleting(image.id)"
                    name="trash-2"
                    class="delete-button-icon"
                  ></i-feather>
                </button>
              </td>
            </ng-container>

            <tr mat-header-row *matHeaderRowDef="displayedColumns"></tr>
            <tr mat-row *matRowDef="let row; columns: displayedColumns;"></tr>
          </table>

          <mat-paginator
            [length]="totalProducts"
            [pageSize]="pageSize"
            [pageSizeOptions]="pageSizeOptions"
            [pageIndex]="pageIndex"
            (page)="handlePageEvent($event)"
          ></mat-paginator>
        </div>

        <div class="empty-state" *ngIf="!products.length && !isLoadingProducts && !productErrorMessage && !isInitialLoad">
          <i-feather name="package"></i-feather>
          <p>No products in this session yet. Go to the Estimator and select this session to start adding products.</p>
        </div>
      </div>
    </mat-card-content>
  </ng-container>

</mat-card>
